import { UserIcon } from '@phosphor-icons/react';
import { Dropdown } from 'antd';
import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';

import logo from '@/assets/images/tracking-web-header-logo.webp';
import { nav } from '@/config/nav';
import { routes } from '@/config/routes';
import { useLogout } from '@/features/auth';
import { NotificationBell, NotificationSettingsDrawer } from '@/features/notifications';
import { CustomerSupportDrawers } from '@/features/support';
import { useAuth } from '@/providers/auth-provider';

import { ProfileMenu } from './profile-menu';
import styles from './top-nav.module.scss';

/**
 * Dark-green top app-bar shell chrome for FieldPro Web. Three zones: left = flat
 * primary nav (Devices | Map) from `config/nav`; center = the FIELDPRO wordmark;
 * right = a notification bell + a profile avatar dropdown (the rich account menu,
 * Figma 2327-693027). Layout chrome — legitimately consumes the auth feature.
 */
export function TopNav() {
  const navigate = useNavigate();
  const { user, clearSession } = useAuth();
  const logout = useLogout();

  const [menuOpen, setMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const signOut = async () => {
    await logout.mutateAsync().catch(() => undefined);
    clearSession();
    navigate(routes.login);
  };

  return (
    <header className={styles.bar}>
      <nav className={styles.nav} aria-label="Primary">
        {nav.map((item) => (
          <NavLink
            key={item.key}
            to={item.to}
            className={({ isActive }) =>
              isActive ? `${styles.link} ${styles.linkActive}` : styles.link
            }
          >
            {item.label}
          </NavLink>
        ))}
      </nav>

      <button
        type="button"
        className={styles.logoBtn}
        aria-label="Go to home"
        onClick={() => navigate(routes.home)}
      >
        <img src={logo} alt="FIELDPRO" className={styles.logo} />
      </button>

      <div className={styles.actions}>
        <NotificationBell />

        {user ? (
          <Dropdown
            open={menuOpen}
            onOpenChange={setMenuOpen}
            trigger={['click']}
            placement="bottomRight"
            popupRender={() => (
              <ProfileMenu
                user={user}
                onClose={() => setMenuOpen(false)}
                onOpenNotificationSettings={() => setSettingsOpen(true)}
                onSignOut={signOut}
              />
            )}
          >
            <button type="button" className={styles.profileBtn} aria-label="Account menu">
              <UserIcon size={20} weight="regular" />
            </button>
          </Dropdown>
        ) : null}
      </div>

      <NotificationSettingsDrawer open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      <CustomerSupportDrawers />
    </header>
  );
}
